/**
 * @fileoverview Modal de sincronização de produtos com API Hiper.
 * Fluxo:
 *   1. Ao abrir → POST /hiper/gettoken
 *   2. Carregar Produtos → GET /hiper/productsfromsyncpoint; fallback GET /hiper/products
 *   3. Sincronizar → POST /hiper/import-products
 *
 * @module HiperSyncProducts
 * @since 1.0.0
 */ 

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { apiRequestJson } from '../../api/apiService';

/**
 * Modal de sincronização de produtos Hiper para administradores.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
export default function HiperSyncProducts({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loaded, setLoaded] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);

  /**
   * Exibe mensagem de feedback.
   * @param {string} message - Mensagem
   * @param {string} severity - Tipo: success, error, warning, info
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Etapa 1: Gera token Hiper ao abrir o modal.
   */
  useEffect(() => {
    if (!open) {
      setTokenReady(false);
      return;
    }

    let cancelled = false;

    const generateToken = async () => {
      try {
        await apiRequestJson('hiper_get_token');
        if (!cancelled) {
          setTokenReady(true);
        }
      } catch (error) {
        if (!cancelled) {
          showSnackbar(`Erro ao gerar token Hiper: ${error.message}`, 'error');
        }
      }
    };

    generateToken();
    return () => { cancelled = true; };
  }, [open]);

  /**
   * Etapa 2: Carregar todos os produtos da API Hiper.
   */
  const handleLoadProducts = useCallback(async () => {
    setLoading(true);
    setProducts([]);
    setLoaded(false);

    try {
      await loadAllProducts();
    } catch (error) {
      showSnackbar(`Erro ao carregar produtos: ${error.message}`, 'error');
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fallback: busca todos os produtos da API Hiper.
   * @async
   */
  const loadAllProducts = async () => {
    const result = await apiRequestJson('hiper_products', {
      headers: { 'Cache-Control': 'no-cache' },
    });

    const payload = result?.data?.data || result?.data;
    const allProducts = payload?.produtos || (Array.isArray(payload) ? payload : []);

    if (allProducts.length > 0) {
      setProducts(allProducts);
      showSnackbar(`${allProducts.length} produto(s) encontrado(s)`, 'info');
    } else {
      setProducts([]);
      showSnackbar('Nenhum produto encontrado na API Hiper', 'info');
    }
    setLoaded(true);
  };

  /**
   * Etapa 3: Sincronizar — importa produtos do MongoDB para MySQL.
   */
  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await apiRequestJson('hiper_import_products');
      const data = result?.data;

      if (data?.errors && data.errors.length > 0) {
        // map: extrai mensagens de erro para exibição
        const errorMessages = data.errors.map(e => e.error).join('; ');
        showSnackbar(`Sincronização com erros: ${errorMessages}`, 'warning');
      } else {
        showSnackbar(
          `Sincronização concluída! ${data?.imported || 0} produto(s) importado(s).`,
          'success'
        );
      }

      // Recarrega lista após sincronização
      await handleLoadProducts();
    } catch (error) {
      showSnackbar(`Erro na sincronização: ${error.message}`, 'error');
    } finally {
      setSyncing(false);
    }
  }, [handleLoadProducts]);

  /**
   * Renderiza tabela de produtos.
   */
  const renderTable = () => {
    const paginatedProducts = products.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <TableContainer component={Paper} sx={{ maxHeight: isMobile ? '50vh' : '60vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Categoria</TableCell>
              {!isMobile && <TableCell>Marca</TableCell>}
              <TableCell align="right">Estoque</TableCell>
              <TableCell align="right">Preço</TableCell>
              {!isMobile && (
                <TableCell align="center">Status</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.map((product, index) => (
              <TableRow key={product.id || index}>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {product.nome}
                </TableCell>
                <TableCell>{product.categoria || '—'}</TableCell>
                {!isMobile && <TableCell>{product.marca || '—'}</TableCell>}
                <TableCell align="right">{product.quantidadeEmEstoque ?? 0}</TableCell>
                <TableCell align="right">
                  {product.preco ? `R$ ${Number(product.preco).toFixed(2)}` : '—'}
                </TableCell>
                {!isMobile && (
                  <TableCell align="center">
                    <Chip
                      label={product.ativo ? 'Ativo' : 'Inativo'}
                      color={product.ativo ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Sincronizar Produtos com HIPER
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Barra de ações */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={18} /> : <CloudDownloadIcon />}
              onClick={handleLoadProducts}
              disabled={loading || syncing || !tokenReady}
            >
              {loading ? 'Carregando...' : 'Carregar Produtos'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
              onClick={handleSync}
              disabled={products.length === 0 || syncing || loading}
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
            {!tokenReady && open && (
              <Typography variant="body2" color="text.secondary">
                Gerando token Hiper...
              </Typography>
            )}
          </Box>

          {/* Conteúdo */}
          {!loaded && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Clique em &quot;Carregar Produtos&quot; para buscar produtos pendentes da API Hiper.
            </Typography>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {loaded && !loading && products.length === 0 && (
            <Alert severity="info" sx={{ my: 2 }}>
              Nenhum produto pendente de sincronização.
            </Alert>
          )}

          {products.length > 0 && (
            <>
              {renderTable()}
              <TablePagination
                component="div"
                count={products.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Linhas por página:"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
